import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAPI } from '../contexts/APIContext';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { usePageTracking } from '../hooks/useAnalytics';
import LoadingScreen from '../components/LoadingScreen';
import { showToast } from '../components/Toast';

const Characters = () => {
  usePageTracking('Characters');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { apiClient } = useAPI();
  const { user, subscriptionStatus, refreshSubscriptionStatus, waitForSubscriptionActivation } = useAuth();
  const { currentProfile, requireParentalVerification } = useProfile();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnimated, setShowAnimated] = useState({});
  const [editingCharacters, setEditingCharacters] = useState({});
  const [editedNames, setEditedNames] = useState({});
  const [updatingCharacters, setUpdatingCharacters] = useState({});

  useEffect(() => {
    loadCharacters();
    
    // Check for upgrade parameter and refresh subscription data
    const upgraded = searchParams.get('upgraded');
    const cancelled = searchParams.get('cancelled');
    
    if (upgraded === 'true') {
      showToast('Payment successful! Your subscription has been updated.', 'success');
      // Clear the parameter from URL
      setSearchParams({});
      // Poll until RevenueCat has ingested the new subscription
      waitForSubscriptionActivation();
    } else if (cancelled === 'true') {
      showToast('Payment was cancelled.', 'info');
      // Clear the parameter from URL
      setSearchParams({});
    }
  }, [apiClient, user?.id, currentProfile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for character deletion events from detail pages
  useEffect(() => {
    const handleCharacterDeleted = (event) => {
      const { characterId } = event.detail || {};
      if (characterId) {
        // Optimistically remove from state
        // NOTE: Don't decrement count here - it's already decremented at the deletion source
        setCharacters(prev => prev.filter(char => char.id !== characterId));
        // Re-fetch in background to sync
        loadCharacters().catch(err => {
          logger.error('Error re-fetching characters after deletion:', err);
        });
      }
    };

    window.addEventListener('character:deleted', handleCharacterDeleted);

    return () => {
      window.removeEventListener('character:deleted', handleCharacterDeleted);
    };
  }, [apiClient, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps


  const loadCharacters = async () => {
    if (!apiClient || !user?.id) return;

    try {
      setLoading(true);
      const response = await apiClient.getUserCharacters(user.id, currentProfile?.id);
      
      if (response.status) {
        // Handle double-wrapped data structure from the API
        const charactersData = response.data;
        const characters = Array.isArray(charactersData) ? charactersData : [];
        setCharacters(characters);
        
        // Initialize animation toggle state - default to animated if available
        const initialToggleState = {};
        const initialEditedNames = {};
        characters.forEach(character => {
          initialToggleState[character.id] = !!character.animated_image_url;
          initialEditedNames[character.id] = character.name || '';
        });
        setShowAnimated(initialToggleState);
        setEditedNames(initialEditedNames);
      } else {
        showToast('Failed to load characters', 'error');
      }
    } catch (error) {
      logger.error('Failed to load characters:', error);
      showToast('Failed to load characters', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleCharacterAnimation = (characterId) => {
    setShowAnimated(prev => ({
      ...prev,
      [characterId]: !prev[characterId]
    }));
  };

  const handleUpgrade = () => {
    requireParentalVerification('upgrade subscription', () => navigate('/settings?tab=stats'));
  };

  const deleteCharacter = async (characterId, characterName) => {
    // Optimistically remove from state immediately
    const deletedCharacter = characters.find(char => char.id === characterId);
    setCharacters(prev => prev.filter(char => char.id !== characterId));
    
    try {
      const response = await apiClient.deleteCharacter(characterId);

      if (response.status) {
        showToast(`Successfully deleted "${characterName}"`, 'success');
        
        // Add DELETECHARACTER tag to user's data_tags
        if (user?.id && apiClient) {
          const timestamp = Date.now();
          const tag = `DELETECHARACTER_${characterId}_${timestamp}`;
          apiClient.addUserDataTag(user.id, tag).then((result) => {
            if (result.status) {
              logger.log(`✅ Added tag ${tag} for user ${user.id}`);
            } else {
              logger.warn(`⚠️ Failed to add tag ${tag}:`, result.error);
            }
          }).catch((error) => {
            logger.error(`❌ Error adding tag ${tag}:`, error);
          });
        }
        
        // Re-fetch in background to sync with database
        loadCharacters().catch(err => {
          logger.error('Error re-fetching characters after deletion:', err);
        });
      } else {
        // Revert optimistic update on failure
        if (deletedCharacter) {
          setCharacters(prev => [...prev, deletedCharacter].sort((a, b) => 
            new Date(b.created_at) - new Date(a.created_at)
          ));
        }
        throw new Error(response.error || 'Failed to delete character');
      }
    } catch (error) {
      // Revert optimistic update on error
      if (deletedCharacter) {
        setCharacters(prev => [...prev, deletedCharacter].sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        ));
      }
      logger.error('Error deleting character:', error);
      showToast('Failed to delete character. Please try again.', 'error');
    }
  };

  const startEditing = (characterId, currentName) => {
    setEditingCharacters(prev => ({ ...prev, [characterId]: true }));
    setEditedNames(prev => ({ ...prev, [characterId]: currentName }));
  };

  const cancelEditing = (characterId, originalName) => {
    setEditingCharacters(prev => ({ ...prev, [characterId]: false }));
    setEditedNames(prev => ({ ...prev, [characterId]: originalName }));
  };

  const saveCharacterName = async (characterId, newName) => {
    if (!newName.trim() || newName.trim() === characters.find(c => c.id === characterId)?.name) {
      setEditingCharacters(prev => ({ ...prev, [characterId]: false }));
      return;
    }

    try {
      setUpdatingCharacters(prev => ({ ...prev, [characterId]: true }));
      const response = await apiClient.updateCharacter(characterId, {
        name: newName.trim()
      });

      if (response.status || response.success) {
        // Update character in local state
        setCharacters(prev => prev.map(char => 
          char.id === characterId ? { ...char, name: newName.trim() } : char
        ));
        setEditingCharacters(prev => ({ ...prev, [characterId]: false }));
        showToast('Character name updated successfully!', 'success');
      } else {
        showToast(response.error || 'Failed to update character name', 'error');
      }
    } catch (error) {
      logger.error('Failed to update character name:', error);
      showToast(error.message || 'Failed to update character name', 'error');
    } finally {
      setUpdatingCharacters(prev => ({ ...prev, [characterId]: false }));
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading your characters..." type="character" />;
  }

  return (
    <div className="characters-page-container min-h-screen">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-blue-400/20 to-cyan-400/20 rounded-full blur-lg animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 rounded-full blur-md animate-pulse animation-delay-4000"></div>
        <div className="absolute bottom-20 right-1/3 w-28 h-28 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 rounded-full blur-lg animate-pulse"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-32">
        {/* Create button with subscription limits */}
        <div className="mb-8">
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/character-lab')}
              className="inline-flex items-center px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 backdrop-blur-sm bg-gradient-to-r from-emerald-500/80 to-teal-500/80 text-white border border-emerald-400/30 hover:from-emerald-500 hover:to-teal-500 hover:shadow-lg hover:scale-105"
            >
              <span className="mr-2 text-lg">🧪</span>
              Character Lab
            </button>
          </div>
        </div>

        {characters.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {characters.map((character, index) => (
              <div
                key={character.id}
                className={`group relative bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-2xl rounded-3xl overflow-hidden border border-white/20 transition-all duration-700 ${
                  editingCharacters[character.id] 
                    ? 'scale-100 border-white/20' 
                    : 'hover:border-purple-400/40 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20 hover:rotate-1'
                }`}
                style={{
                  animationDelay: `${index * 150}ms`
                }}
              >
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-pink-500/5 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                
                {/* Character image with enhanced styling */}
                <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-purple-100/10 to-pink-100/10">
                  <img 
                    src={
                      showAnimated[character.id] && character.animated_image_url
                        ? character.animated_image_url
                        : character.handdrawn_image_url || character.image_url || '/assets/handdrawn-character.png'
                    }
                    alt={character.name}
                    className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-125 group-hover:brightness-110 group-hover:saturate-110"
                    onError={(e) => {
                      e.target.src = character.handdrawn_image_url || '/assets/handdrawn-character.png';
                    }}
                  />
                  
                  {/* Animation Toggle - only show if both versions exist */}
                  
                  {/* Dynamic overlay with animated gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                  
                  {/* Floating sparkle effects */}
                  <div className="absolute top-4 left-4 w-2 h-2 bg-yellow-300 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700 animate-ping"></div>
                  
                  <div className="absolute bottom-6 right-6 w-1.5 h-1.5 bg-purple-300 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-1000 animate-pulse"></div>
                  
                </div>
                
                {/* Enhanced character info section */}
                <div className="relative p-6 space-y-4 bg-gradient-to-t from-black/20 to-transparent">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      {editingCharacters[character.id] ? (
                        <div className="flex-1 mr-2">
                          <div className="flex gap-2 items-center">
                            <input
                              type="text"
                              value={editedNames[character.id] || ''}
                              onChange={(e) => setEditedNames(prev => ({ 
                                ...prev, 
                                [character.id]: e.target.value 
                              }))}
                              className="flex-1 px-3 py-2 text-lg font-bold text-white bg-white/10 backdrop-blur-md border border-white/30 rounded-xl focus:outline-none focus:border-purple-400/60 focus:ring-1 focus:ring-purple-400/20 transition-all duration-200"
                              placeholder="Character name..."
                              autoFocus
                              maxLength={30}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  saveCharacterName(character.id, editedNames[character.id]);
                                }
                                if (e.key === 'Escape') {
                                  cancelEditing(character.id, character.name);
                                }
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <h3 className="font-bold text-2xl text-white leading-tight tracking-tight group-hover:text-purple-200 transition-colors duration-500">
                          {character.name}
                        </h3>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                      <p className="text-sm text-gray-300 font-medium group-hover:text-gray-200 transition-colors duration-500">
                        Created {character.created_at ? new Date(character.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : 'Unknown'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Action buttons - show save/cancel when editing, view/edit/delete when not */}
                  <div className="flex items-center gap-3 pt-2">
                    {editingCharacters[character.id] ? (
                      <>
                        {/* Save button */}
                        <button 
                          onClick={() => saveCharacterName(character.id, editedNames[character.id])}
                          disabled={updatingCharacters[character.id] || !editedNames[character.id]?.trim()}
                          className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 disabled:from-gray-500 disabled:to-gray-600 text-white px-5 py-3 rounded-2xl text-sm font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/30 active:scale-95 transform hover:-translate-y-0.5 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          {updatingCharacters[character.id] ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <span className="flex items-center justify-center space-x-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>Save</span>
                            </span>
                          )}
                        </button>
                        
                        {/* Cancel button */}
                        <button 
                          onClick={() => cancelEditing(character.id, character.name)}
                          disabled={updatingCharacters[character.id]}
                          className="px-4 py-3 bg-white/15 hover:bg-white/25 text-white rounded-2xl text-sm font-semibold transition-all duration-300 hover:shadow-lg border border-white/30 hover:border-white/50 active:scale-95 transform hover:-translate-y-0.5 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        {/* View button */}
                        <button 
                          onClick={() => navigate(`/characters/${character.id}`)}
                          className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-5 py-3 rounded-2xl text-sm font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/30 active:scale-95 transform hover:-translate-y-0.5"
                        >
                          <span className="flex items-center justify-center space-x-2">
                            <span>View</span>
                            <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </span>
                        </button>
                        
                        {/* Edit name button */}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(character.id, character.name);
                          }}
                          className="px-4 py-3 bg-white/15 hover:bg-white/25 text-white rounded-2xl text-sm font-semibold transition-all duration-300 hover:shadow-lg border border-white/30 hover:border-white/50 active:scale-95 transform hover:-translate-y-0.5"
                          title="Edit character name"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        
                        {/* Edit Character (Nano-Banana) button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const imageUrl = character.animated_image_url || character.handdrawn_image_url || character.image_url;
                            navigate(`/character-lab?id=${character.id}&image_url=${encodeURIComponent(imageUrl || '')}&name=${encodeURIComponent(character.name || '')}`);
                          }}
                          className="px-4 py-3 bg-emerald-500/80 hover:bg-emerald-500 text-white rounded-2xl text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/30 active:scale-95 transform hover:-translate-y-0.5"
                          title="Edit character image"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        
                        {/* Delete button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            requireParentalVerification('delete this character', () => {
                              if (window.confirm('Are you sure you want to delete this character?')) {
                                deleteCharacter(character.id, character.name);
                              }
                            });
                          }}
                          className="px-4 py-3 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded-2xl transition-all duration-300 active:scale-95 transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-500/20"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            {/* Empty state illustration */}
            <div className="relative mb-8">
              <div className="text-8xl mb-4 animate-bounce">🎭</div>
              <div className="flex justify-center space-x-4 mb-6">
                <span className="text-3xl animate-pulse" style={{animationDelay: '0s'}}>✨</span>
                <span className="text-2xl animate-pulse" style={{animationDelay: '0.5s'}}>🌟</span>
                <span className="text-3xl animate-pulse" style={{animationDelay: '1s'}}>💫</span>
              </div>
            </div>
            
            <h3 className="text-3xl font-bold text-white mb-6 cloud-text">
              No Characters Yet
            </h3>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto cloud-text leading-relaxed">
              Create your first magical character using the button above!
            </p>
              
            <div className="flex justify-center space-x-8 mt-8 text-gray-400">
              <div className="text-center">
                <div className="text-2xl mb-2">🖌️</div>
                <p className="text-sm cloud-text">Draw & Design</p>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-2">🎭</div>
                <p className="text-sm cloud-text">Add Personality</p>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-2">📚</div>
                <p className="text-sm cloud-text">Create Stories</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Characters;
